import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Star, User, Building2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ReviewModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    rating: 5,
    comment: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Review submitted:', formData);
    toast({
      title: 'Review Submitted!',
      description: 'Thank you for your feedback. Your review will be published soon.',
    });
    setFormData({ name: '', company: '', rating: 5, comment: '' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] bg-[#2a2a2a] text-white border-[#4a4a4a]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">Leave a Review</DialogTitle>
          <DialogDescription className="text-[#a0a0a0]">
            Share your experience with Drakify Express. Your feedback helps us improve.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="review-name" className="text-white">Your Name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-[#a0a0a0]" />
              <Input
                id="review-name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="pl-10 bg-[#1a1a1a] border-[#4a4a4a] text-white focus:border-white"
                placeholder="John Doe"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-company" className="text-white">Company Name</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-3 h-4 w-4 text-[#a0a0a0]" />
              <Input
                id="review-company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="pl-10 bg-[#1a1a1a] border-[#4a4a4a] text-white focus:border-white"
                placeholder="Your Company"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Rating *</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= formData.rating
                        ? 'fill-white text-white'
                        : 'text-[#4a4a4a]'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-comment" className="text-white">Your Review *</Label>
            <Textarea
              id="review-comment"
              name="comment"
              required
              value={formData.comment}
              onChange={handleChange}
              className="min-h-[120px] bg-[#1a1a1a] border-[#4a4a4a] text-white focus:border-white"
              placeholder="Share your experience with our services..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-white text-black hover:bg-[#e5e5e5] font-semibold"
            >
              Submit Review
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-[#4a4a4a] text-white hover:bg-[#3a3a3a]"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
